/**
 * D326: rosettes, a rare and lasting reward for turning up most weeks, like Egg, Inc.'s monthly
 * Prophecy Egg. Earned by winning the weekly event (D304) in `content.rosettes.need` of the last
 * `content.rosettes.window` weeks; `idleFoldRosetteWeek` folds in each week's result as it ends,
 * called from `settleIdleDaily`. A missed week never loses a rosette already earned; it only
 * drops out of the rolling window once it is older than `window` weeks. Each rosette raises all
 * income by `content.rosettes.factor`, compounding, up to `content.rosettes.cap` rosettes, kept
 * for good across festivals and generations (`permanent.rosettes`).
 */
import type { IdleContent } from '@hedgerow/data';
import type { IdleWorldState } from './state.ts';

/** Rosettes earned so far, oldest first. Empty on a world that has earned none. */
export function idleRosettes(state: IdleWorldState): string[] {
  return state.permanent.rosettes ?? [];
}

/** The income multiplier every rosette earned gives together, capped in the data. */
export function idleRosetteMultiplier(state: IdleWorldState, content: IdleContent): number {
  const n = Math.min(idleRosettes(state).length, content.rosettes.cap);
  return n > 0 ? content.rosettes.factor ** n : 1;
}

/**
 * Fold one more week's result into the rolling window towards the next rosette, oldest first,
 * and award it once `need` of the last `window` weeks are won. The same state when nothing
 * changes. A missed week (`won` false) never takes away a rosette already earned. Once every
 * rosette the data allows is earned (`cap`), the window still moves but no more are given.
 */
export function idleFoldRosetteWeek(
  state: IdleWorldState,
  content: IdleContent,
  won: boolean,
): IdleWorldState {
  const r = content.rosettes;
  const window = [...(state.permanent.rosetteWindow ?? []), won].slice(-r.window);
  const earned = idleRosettes(state);
  if (earned.length >= r.cap || window.filter(Boolean).length < r.need)
    return { ...state, permanent: { ...state.permanent, rosetteWindow: window } };
  const names = content.collection.rosettes;
  const next = names.length > 0 ? [...earned, names[earned.length % names.length]!.id] : earned;
  // The window starts fresh for the next rosette once this one is won.
  return { ...state, permanent: { ...state.permanent, rosetteWindow: [], rosettes: next } };
}

export interface IdleRosetteView {
  /** Rosettes earned so far. */
  count: number;
  /** The income multiplier they give together, for the screen's `x1.16`. */
  bonus: number;
  /** Weeks won in the current window, the running week not counted. */
  weeks: number;
  /** Weeks the window holds, at most `content.rosettes.window`. */
  of: number;
  /** Weeks of the window that must be won. */
  need: number;
  /** Every rosette the data allows has been earned. */
  capped: boolean;
}

/** The rosettes earned, the income bonus they give, and the progress to the next one. */
export function idleRosetteView(state: IdleWorldState, content: IdleContent): IdleRosetteView {
  const r = content.rosettes;
  const window = state.permanent.rosetteWindow ?? [];
  return {
    count: idleRosettes(state).length,
    bonus: idleRosetteMultiplier(state, content),
    weeks: window.filter(Boolean).length,
    of: window.length,
    need: r.need,
    capped: idleRosettes(state).length >= r.cap,
  };
}
